import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { API, getAuthHeaders } from '../../utils/adminAuth';

export default function ScentNoteModal({ note, allNotes, onClose, onSave }) {
  const isEdit = !!note;
  const [form, setForm] = useState({
    id: note?.id || '',
    name: note?.name || '',
    category: note?.category || 'heart',
    family: note?.family || '',
    good_with: note?.good_with?.join(', ') || '',
    avoid: note?.avoid?.join(', ') || '',
    description: note?.description || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        id: form.id || form.name.toLowerCase().replace(/\s+/g, '_'),
        good_with: form.good_with.split(',').map(s => s.trim()).filter(Boolean),
        avoid: form.avoid.split(',').map(s => s.trim()).filter(Boolean),
      };
      if (isEdit) {
        await axios.put(`${API}/admin/scent-notes/${note.id}`, payload, { headers: getAuthHeaders() });
        toast.success('Note updated');
      } else {
        await axios.post(`${API}/admin/scent-notes`, payload, { headers: getAuthHeaders() });
        toast.success('Note created');
      }
      onSave();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="scent-note-modal">
      <div className="bg-[#0A0A0A] border border-white/10 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h2 className="text-lg text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {isEdit ? 'Edit Note' : 'Add Note'}
          </h2>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <input type="text" placeholder="Note Name (e.g. Vanilla)" value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            className="w-full bg-transparent border-b border-white/20 text-white py-2 text-sm focus:border-[#D4AF37] focus:outline-none placeholder:text-white/30"
            data-testid="note-name-input" />
          {!isEdit && (
            <input type="text" placeholder="ID (auto-generated)" value={form.id}
              onChange={e => setForm({ ...form, id: e.target.value })}
              className="w-full bg-transparent border-b border-white/20 text-white/50 py-2 text-xs focus:border-[#D4AF37] focus:outline-none placeholder:text-white/20"
              data-testid="note-id-input" />
          )}
          <div className="grid grid-cols-2 gap-3">
            <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
              className="bg-[#141414] border border-white/20 text-white text-sm px-3 py-2" data-testid="note-category-select">
              <option value="top">Top</option>
              <option value="heart">Heart</option>
              <option value="base">Base</option>
            </select>
            <select value={form.family} onChange={e => setForm({ ...form, family: e.target.value })}
              className="bg-[#141414] border border-white/20 text-white text-sm px-3 py-2" data-testid="note-family-select">
              <option value="">Select family</option>
              {['citrus', 'fruity', 'spicy', 'fresh', 'floral', 'sweet', 'woody'].map(f => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          </div>
          <input type="text" placeholder="Description" value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
            className="w-full bg-transparent border-b border-white/20 text-white py-2 text-sm focus:border-[#D4AF37] focus:outline-none placeholder:text-white/30"
            data-testid="note-description-input" />
          <div>
            <label className="text-white/40 text-xs block mb-1">Good With (comma-separated IDs)</label>
            <input type="text" value={form.good_with}
              onChange={e => setForm({ ...form, good_with: e.target.value })}
              className="w-full bg-transparent border-b border-white/20 text-white py-2 text-xs focus:border-[#D4AF37] focus:outline-none placeholder:text-white/20"
              placeholder="e.g. vanilla, musk, amber"
              data-testid="note-good-with-input" />
          </div>
          <div>
            <label className="text-white/40 text-xs block mb-1">Avoid (comma-separated IDs)</label>
            <input type="text" value={form.avoid}
              onChange={e => setForm({ ...form, avoid: e.target.value })}
              className="w-full bg-transparent border-b border-white/20 text-white py-2 text-xs focus:border-[#D4AF37] focus:outline-none placeholder:text-white/20"
              placeholder="e.g. marine, eucalyptus"
              data-testid="note-avoid-input" />
          </div>
        </div>
        <div className="p-4 border-t border-white/10">
          <button onClick={handleSave} disabled={saving}
            className="w-full bg-[#D4AF37] text-black py-2.5 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#F3E5AB] transition-colors disabled:opacity-50"
            data-testid="save-note-btn">
            {saving ? 'Saving...' : isEdit ? 'Update Note' : 'Create Note'}
          </button>
        </div>
      </div>
    </div>
  );
}
